'use client';

// components
import orDesignStudioBlockVariants from './or-design-studio-block.variants';
import { AtDivider } from '@/components/atoms/at-divider/at-divider';
import { AtMedia } from '@/components/atoms/at-media/at-media';
import { AtHeader } from '@/components/atoms/at-header/at-header';
import { AtText } from '@/components/atoms/at-text/at-text';
import { AtClickable } from '@/components/atoms/at-clickable/at-clickable';
import Link from 'next/link';

// types
import { IOrDesignStudioBlockProps } from './or-design-studio-block.types';
import {
  EDividerColors,
  EDividerDirectionalVariants,
  EDividerVariants,
} from '@/components/atoms/at-divider/at-divider.types';

// utils + hooks
import { JSX, useMemo } from 'react';
import { sendHomePlanClickGTMEvent } from '@/components/helpers/analytics/gtm';
import { formatModelHomeUrl } from '@/components/helpers/formatters/model-homes';

export const OrDesignStudioBlock = ({
  designStudio,
  ctaProps,
  title,
  topSpacing = 'md',
  bottomSpacing = 'md',
  topDivider = false,
  bottomDivider = false,
  titleVariant = 'h2',
  designStudioNameVariant = 'h6',
  relatedModelsVariant = 'h6',
}: IOrDesignStudioBlockProps): JSX.Element => {
  const { base, mainImageContainer, header, studioName } =
    orDesignStudioBlockVariants({ topSpacing, bottomSpacing });

  const studioHasFullAddress = useMemo(() => {
    return !!(
      designStudio?.Address &&
      designStudio.City &&
      designStudio.State &&
      designStudio.Zip
    );
  }, [designStudio]);

  if (!designStudio) return <></>;

  return (
    <div data-theme="light">
      <article data-testid="or-design-studio-block" className="container">
        {topDivider && (
          <AtDivider
            decorative
            color={EDividerColors.GREY}
            direction={EDividerDirectionalVariants.HORIZONTAL}
            intent={EDividerVariants.THIN}
          />
        )}
        <div className={base()}>
          <section className={mainImageContainer()}>
            {designStudio.image && <AtMedia {...designStudio.image} />}
          </section>
          <section className="md:col-span-1" />
          <section className="md:col-span-5 md:py-8">
            <AtHeader element="h2" variant={titleVariant} className={header()}>
              {title}
            </AtHeader>
            <AtHeader
              element="h3"
              variant={designStudioNameVariant}
              className={studioName()}
            >
              {designStudio.Name}
            </AtHeader>
            {studioHasFullAddress && (
              <div className="flex flex-col gap-1 md:flex-row md:gap-4">
                <AtText
                  type="p"
                  size="lg"
                  className="border-skin-stroke-primary md:border-r-2 md:pr-4"
                >
                  {designStudio.Address}
                </AtText>
                <AtText
                  type="p"
                  size="lg"
                  className="border-skin-stroke-primary"
                >
                  {designStudio.City}, {designStudio.State} {designStudio.Zip}
                </AtText>
              </div>
            )}
            {ctaProps && (
              <div className="mt-6 flex flex-col items-start gap-6 md:mt-8 md:flex-row md:items-center md:gap-8">
                {ctaProps.slice(0, 2).map((cta, i) => (
                  <AtClickable
                    {...cta}
                    key={`${i}-${cta.label}`}
                    className="text-center"
                    interactLocation="or-design-studio-block"
                  />
                ))}
              </div>
            )}
            {designStudio.relatedModels?.length ? (
              <div className="mt-15">
                <AtHeader
                  element="h4"
                  variant={relatedModelsVariant}
                  className="text-uppercase mb-6 font-Figtree font-semibold text-skin-primary md:mb-8"
                >
                  AVAILABLE MODELS:
                </AtHeader>
                <div className="custom-scrollbar flex flex-col gap-5 pr-4 md:max-h-270 md:gap-8 md:overflow-y-auto md:pr-16">
                  {/* A maximum of 6 related home plans must be used. */}
                  {designStudio.relatedModels
                    .slice(0, 6)

                    .map(({ ExteriorStyle, Plan, Name, Image, Id, cms }, i) => (
                      <div
                        key={`${i}-${ExteriorStyle.Name}`}
                        className="relative flex"
                      >
                        <div className="mr-6 h-120 w-170">
                          <AtMedia
                            sizes="(max-width: 1023px) 100vw, 50vw"
                            src={{
                              desktop:
                                cms?.card_image?.mobile_image_url?.url ||
                                (Image as string),
                              tablet:
                                cms?.card_image?.mobile_image_url?.url ||
                                (Image as string),
                              mobile:
                                cms?.card_image?.mobile_image_url?.url ||
                                (Image as string),
                            }}
                            priority={false}
                            alt=""
                            decorative
                          />
                        </div>
                        <div className="flex w-full flex-col justify-center">
                          <Link
                            href={formatModelHomeUrl(
                              designStudio?.CsName || designStudio?.Name,
                              Plan.Name,
                              ExteriorStyle?.Name,
                            )}
                            className="expandCoverage !p-0"
                            onClick={() => {
                              sendHomePlanClickGTMEvent(
                                Name,
                                Id || -1,
                                formatModelHomeUrl(
                                  designStudio?.Name,
                                  Name,
                                  ExteriorStyle?.Name,
                                ),
                              );
                            }}
                          >
                            <AtHeader
                              element="p"
                              variant="h4"
                              className="mb-2 text-32 md:mb-3"
                            >
                              {Plan.Name}
                            </AtHeader>
                          </Link>

                          <AtHeader
                            element="p"
                            variant="h6"
                            className="text-2xl text-skin-subhead-primary"
                          >
                            {ExteriorStyle.Name}
                          </AtHeader>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            ) : (
              <></>
            )}
          </section>
        </div>
        {bottomDivider && (
          <AtDivider
            decorative
            color={EDividerColors.GREY}
            direction={EDividerDirectionalVariants.HORIZONTAL}
            intent={EDividerVariants.THIN}
          />
        )}
      </article>
    </div>
  );
};
